import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CampaignIcon from '@mui/icons-material/Campaign';
import ExtensionIcon from '@mui/icons-material/Extension';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { SetupCheck } from '../services/api';

interface Props {
  open: boolean;
  checks: SetupCheck[];
  onClose: () => void;
  onGoToSettings: () => void;
}

const ORANGE = '#ed8936';
const RED = '#ef4444';
const GREEN = '#22c55e';

const GROUPS: { key: string; title: string; icon: React.ReactNode }[] = [
  { key: 'engagement', title: '7-Day Engagement Campaign Setup', icon: <CampaignIcon fontSize="small" /> },
  { key: 'integration', title: 'Integrations', icon: <ExtensionIcon fontSize="small" /> },
];

function CheckRow({ check }: { check: SetupCheck }) {
  const done = check.ok;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.25,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: done ? 'transparent' : 'rgba(239, 68, 68, 0.45)',
        borderLeft: done ? '3px solid transparent' : `3px solid ${RED}`,
        bgcolor: done ? 'rgba(34, 197, 94, 0.06)' : 'rgba(239, 68, 68, 0.08)',
      }}
    >
      {done ? (
        <CheckCircleIcon sx={{ color: GREEN, fontSize: 22, mt: '1px' }} />
      ) : (
        <ErrorIcon sx={{ color: RED, fontSize: 22, mt: '1px' }} />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: done ? 500 : 700, color: done ? 'text.secondary' : 'text.primary' }}
        >
          {check.label}
        </Typography>
        {!done && check.hint && (
          <Typography variant="caption" sx={{ color: RED, opacity: 0.85 }}>
            {check.hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/**
 * Action-oriented prompt shown when an agent's account isn't fully set up.
 * Items are grouped (7-Day Engagement vs Integrations) and outstanding ones are
 * flagged red so it reads as a to-do, not just information.
 */
export default function SetupChecklistDialog({ open, checks, onClose, onGoToSettings }: Props) {
  const outstanding = checks.filter((c) => !c.ok).length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
      {/* Alert banner header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, bgcolor: 'rgba(239, 68, 68, 0.12)', borderBottom: `1px solid rgba(239, 68, 68, 0.3)` }}>
        <WarningAmberIcon sx={{ color: RED, fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
            Action needed to go live
          </Typography>
          <Typography variant="body2" sx={{ color: RED, fontWeight: 600 }}>
            {outstanding} item{outstanding === 1 ? '' : 's'} must be completed before your 7-Day Engagement runs.
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {GROUPS.map((group, gi) => {
          const items = checks.filter((c) => c.group === group.key);
          if (items.length === 0) return null;
          const groupOutstanding = items.filter((c) => !c.ok).length;
          return (
            <Box key={group.key} sx={{ mb: gi < GROUPS.length - 1 ? 3 : 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: groupOutstanding ? RED : GREEN, display: 'flex' }}>{group.icon}</Box>
                <Typography variant="subtitle2" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.72rem' }}>
                  {group.title}
                </Typography>
                <Chip
                  size="small"
                  label={groupOutstanding ? `${groupOutstanding} to do` : 'Done'}
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    bgcolor: groupOutstanding ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                    color: groupOutstanding ? RED : GREEN,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {items.map((c) => <CheckRow key={c.key} check={c} />)}
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none', color: ORANGE }}>Later</Button>
        <Button
          variant="contained"
          onClick={onGoToSettings}
          sx={{ bgcolor: ORANGE, '&:hover': { bgcolor: '#dd7826' }, textTransform: 'none', fontWeight: 700, px: 3 }}
        >
          Set up now
        </Button>
      </DialogActions>
    </Dialog>
  );
}
